import {AfterViewInit, Component, HostListener, OnDestroy, OnInit} from '@angular/core';
import {appData, compareIds, DEFAULT_PORT, isNullOrUndefOrEmpty, WEB_SERVER} from "../../../common";
import {EnumConnectionType, IAppStatic, IPlayerProps, IResource, IStationData, IStationStatsV1, IUser} from "../../../interfaces";
import {fadeTransition, staggerTransitionInOut} from "../../../../libs/basicTransition";
import {AccessService, LocalStoreService, StoreSelectorsService} from '../../../services';
import {filter, map, take} from 'rxjs/operators';
import {combineLatest, Observable} from 'rxjs';
import {data10, data100, data2, data3, data4, data5, data6} from "../overview-data";
import {Compbaser} from "../../../components/compbaser";
import {Router} from "@angular/router";
import {Color, ScaleType} from '@swimlane/ngx-charts';
import * as _ from 'lodash';
import {Lib} from "../../../../libs/Lib";

const {version: appVersion} = require('../../../../../package.json');

@Component({
    selector: 'app-overview',
    templateUrl: './overview.component.html',
    styleUrls: ['./overview.component.scss'],
    animations: [
        fadeTransition,
        staggerTransitionInOut
    ],
    standalone: false
})

export class OverviewComponent extends Compbaser implements OnInit, AfterViewInit, OnDestroy {

    protected readonly isAccessDenied = Lib.IsAccessBlocked;

    isDashboard = false;
    appVersion = appVersion;
    gradient = false;
    timeline = true;

    stations: IStationData[] = [];
    appData: IAppStatic;
    user: IUser;
    usage: any = {
        resourceTotal: 0,
        scenesTotal: 0,
        stationsTotal: 0,
        paintTotal: 0,
        catalogTotal: 0,
        eventsTotal: 0,
        transitionsTotal: 0,
        musicTotal: 0,
        productsTotal: 0
    };
    screens: any = {
        screenOn: 0,
        screenOff: 0,
        totalScreens: 0,
        screenPoll: 0,
        geoScreens: 0
    };
    resources: any = {
        videos: 0,
        images: 0,
        other: 0,
        max: 0
    };
    resourcesByType = {
        mp4: 0,
        flv: 0,
        m4v: 0,
        swf: 0,
        jpeg: 0,
        gif: 0,
        jpg: 0,
        png: 0,
        svg: 0,
        mp3: 0,
        pdf: 0
    };

    contact;
    business = {
        totalBusinesses: 0,
        totalUsers: 0,
        apps: 0,
        installedApps: 0,
        priveleges: 0
    };

    // trackStations: IStationTrackReply[] = [];
    isLoaded = false;
    dataRemoteScreens = [];
    dataTrackStationsSummary = [];
    dataTrackStationsByConnection = [];
    dataTrackingStateByStatus = [];
    dataTrackingStateByPercent = [];
    dataResourceByVideo = [];
    dataResourceByImage = [];
    dataUsageTotal = [];
    dataBusiness = [];
    reloadCard = true;
    embedCustomHomePage;

    /** Chart Templates - cohesive new-age palette (works across light/dark/gradient themes) */
    colorScheme: Color = {
        name: 'Customer Usage',
        selectable: true,
        group: ScaleType.Ordinal,
        domain: ['#6aa6ff', '#7d5cff', '#4dd0e1', '#a78bfa', '#f472b6', '#34d399', '#fb923c', '#38bdf8', '#c084fc', '#facc15', '#2dd4bf', '#fb7185']
    };

    readonly data100 = data100;
    readonly data2 = data2;
    readonly data3 = data3;
    readonly data4 = data4;
    readonly data5 = data5;
    readonly data6 = data6;
    readonly data10 = data10;

    cardColor = "#303030";
    legendPosition = 'below';
    minRadius = 100;

    resizingTimeout;

    @HostListener('window:resize', ['$event'])
    onResize(event) {
        clearTimeout(this.resizingTimeout);
        this.resizingTimeout = setTimeout(() => {
            this.refreshChart();
        }, 100);
    }

    constructor(private storeSelector: StoreSelectorsService,
                private router: Router,
                private accessService: AccessService,
                private localStoreService: LocalStoreService) {
        super();
        this.cod(
            combineLatest([this.storeSelector.getStationStats(), this.storeSelector.getStationGeoIPs()])
                .pipe(
                    map(([stations, geoIps]) => {
                        stations.forEach((i_station) => {
                            const idx = geoIps.findIndex((item) => item.businessId === i_station.$.businessId);
                            const station: IStationStatsV1 = _.cloneDeep(i_station);
                            station.$.geoLocation = null;
                            if (idx > -1) station.$.geoLocation = geoIps[idx];
                            this.screens.geoScreens++;
                        });
                    }),
                    take(1)
                )
                .subscribe()
        );
        this.cod(
            this.storeSelector.getStationsComplete(EnumConnectionType.CONNECTED).subscribe((v) => {
                this.stations = v;
            })
        );
        this.cod(
            this.storeSelector.getEnterpriseAccDetails().subscribe((b) => {
                this.contact = b.Account.Contact[0].$;
            })
        );
        this.cod(
            this.storeSelector.getUser().subscribe((user: IUser) => {
                this.user = user;
                this.isDashboard = user.dashboard;
                // this.updateTrackingState();
            })
        );
        this.cod(
            this.storeSelector.getEnterprisePrivileges().subscribe((b) => {
                this.business.priveleges = b.length;
                this.updateDataBusiness();
            })
        );
        this.cod(
            this.storeSelector.getEnterpriseApps().subscribe((b) => {
                this.business.apps = b[0].App.length;
                this.updateDataBusiness();
            })
        );
        this.cod(
            this.storeSelector.getEnterpriseInstalledApps().subscribe((b) => {
                this.business.installedApps = b[0].App.length;
                this.updateDataBusiness();
            })
        );
        this.cod(
            this.storeSelector.getEnterpriseBusinesses().subscribe((b) => {
                if (isNullOrUndefOrEmpty(b.Businesses)) {
                    this.business.totalBusinesses = 0;
                } else {
                    this.business.totalBusinesses = b.Businesses.BusinessInfo.length;
                }
                this.updateDataBusiness();
            })
        );
        this.cod(
            this.storeSelector.getEnterpriseBusinessUsers().subscribe((b) => {
                this.business.totalUsers = b.BusinessUsers.length;
                this.updateDataBusiness();
            })
        );
        this.cod(
            this.storeSelector
                .getResources()
                .pipe(filter(v => !_.isNull(v)))
                .subscribe((resource: IResource[]) => {
                    this.usage.resourceTotal = resource.length;
                    this.updateDataUsage(1);
                    resource.forEach((i_resource: IResource) => {
                        switch (i_resource.resource_type) {
                            case "flv": {
                                this.resourcesByType.flv++;
                                this.resources.videos++;
                                break;
                            }
                            case "m4v": {
                                this.resourcesByType.m4v++;
                                this.resources.videos++;
                                break;
                            }
                            case "mp4": {
                                this.resourcesByType.mp4++;
                                this.resources.videos++;
                                break;
                            }
                            case "gif":
                            case "jpeg":
                            case "jpg": {
                                this.resourcesByType.jpg++;
                                this.resources.images++;
                                break;
                            }
                            case "png": {
                                this.resourcesByType.png++;
                                this.resources.images++;
                                break;
                            }
                            case "svg": {
                                this.resourcesByType.svg++;
                                this.resources.images++;
                                break;
                            }
                            case "mp3": {
                                this.resourcesByType.mp3++;
                                this.resources.other++;
                                break;
                            }
                            case "pdf": {
                                this.resourcesByType.pdf++;
                                this.resources.other++;
                                break;
                            }
                            case "swf": {
                                this.resourcesByType.swf++;
                                this.resources.other++;
                                break;
                            }
                            default: {
                                this.resources.other++;
                                break;
                            }
                        }
                    });
                    this.resources.max = Math.max(this.resources.other, this.resources.videos, this.resources.images);
                    this.resources = _.cloneDeep(this.resources);
                    this.dataResourceByVideo = Array.from(['flv', 'm4v', 'mp4', 'swf'], key => ({
                        name: key,
                        value: this.resourcesByType[key]
                    }));
                    this.dataResourceByImage = Array.from(['jpg', 'png', 'svg', 'mp3'], key => ({
                        name: key,
                        value: this.resourcesByType[key]
                    }));
                })
        );
        this.cod(
            this.storeSelector
                .getScenes()
                .pipe(filter(v => !_.isNull(v)))
                .subscribe((scenes: IPlayerProps[]) => {
                    this.usage.scenesTotal = scenes.length;
                    this.updateDataUsage(1);
                })
        );
        this.cod(
            this.storeSelector.getStationStats()
                .subscribe((stations) => {
                    this.screens.screenOff = 0;
                    this.screens.screenOn = 0;
                    this.screens.screenPoll = 0;
                    this.screens.totalScreens = stations.length;
                    this.usage.stationsTotal = stations.length;
                    stations.forEach((station: IStationStatsV1) => {
                        if (station.$.connection === '0') {
                            this.screens.screenOff++;
                        } else if (station.$.connection === '1') {
                            this.screens.screenOn++;
                        } else {
                            this.screens.screenPoll++;
                        }
                    });

                    this.dataRemoteScreens = [
                        {
                            "name": "connected",
                            "value": this.screens.screenOn
                        },
                        {
                            "name": "disconnected",
                            "value": this.screens.screenOff
                        },
                        {
                            "name": "polling",
                            "value": this.screens.screenPoll
                        },
                        {
                            "name": "total screens",
                            "value": this.screens.totalScreens
                        },
                        {
                            "name": "geo screens",
                            "value": this.screens.geoScreens
                        }
                    ];
                    this.updateDataUsage(1);
                })
        );
        this.cod(
            this.storeSelector.getPaints().subscribe((i_paints) => {
                this.usage.paintTotal = i_paints.length;
                this.updateDataUsage(1);
            })
        );
        this.cod(
            this.storeSelector.getCatalogItems().subscribe((i_catalogItems) => {
                this.usage.catalogTotal = i_catalogItems.length;
                this.updateDataUsage(2);
            })
        );
        this.cod(
            this.storeSelector.getEventParams().subscribe((i_events) => {
                this.usage.eventsTotal = i_events.length;
                this.updateDataUsage(2);
            })
        );
        this.cod(
            this.storeSelector.getMusicChannels().subscribe((i_music) => {
                this.usage.musicTotal = i_music.length;
                this.updateDataUsage(2);
            })
        );
        this.cod(
            this.storeSelector.getTransitionPools().subscribe((i_transitions) => {
                this.usage.transitionsTotal = i_transitions.length;
                this.updateDataUsage(2);
            })
        );
        this.cod(
            this.storeSelector.getCartProducts().subscribe((i_products) => {
                this.usage.productsTotal = i_products.length;
                this.updateDataUsage(2);
            })
        );

        this.cod(
            this.localStoreService.theme.subscribe((val) => {
                this.reloadCard = false;
                const theme = this.localStoreService.getStorageValConfig('theme');
                if (theme == '1') {
                    this.cardColor = "#e3e3e3";
                } else if (theme == '2') {
                    this.cardColor = "#040a1b";
                } else {
                    this.cardColor = "#303030";
                }
                this.refreshChart();
            })
        );
        // setTimeout(() => {
        //     this.cod(
        //         this.storeSelector.getTrackStats().subscribe((v) => {
        //             if (v && v.length > 0) {
        //                 this.trackStations = v;
        //                 this.setTrackStationsSummary();
        //                 this.setTrackStationsByConnection();
        //                 this.setDataTrackingStateByStatus();
        //             }
        //         })
        //     );
        // });

        setTimeout(() => {
            this.storeSelector.logger({message: `loaded network live stats`, severity: 0});
        }, 3000);

        this.appData = appData;
    }

    // updateTrackingState() {
    //     this.cod(
    //         this.storeSelector.getTrackStats(this.isDashboard).subscribe((v) => {
    //             if (v) {
    //                 this.trackStations = v;
    //                 this.setTrackStationsSummary();
    //                 this.setTrackStationsByConnection();
    //                 this.setDataTrackingStateByStatus();
    //             }
    //         })
    //     );
    // }

    ngOnInit() {
        setTimeout(() => {
            this.isLoaded = true;
        }, 500);
    }

    ngAfterViewInit() {
        this.checkEmbeddedCustomHomePage();
    }

    /** if enterprise user check if we need to swap overview page to a custom home page **/
    private checkEmbeddedCustomHomePage() {
        if (!this.user.enterprise) return;
        this.cod(
            this.accessService.getGroupsCollection("SignStudio Dashboard").pipe(map((value) => value))
                .pipe(take(1),
                    map((value) => {
                        return value?.$?.showCustomHome ? value.$.showCustomHome : '0';
                    })
                ).subscribe((v) => {
                if (compareIds(v, '1') && !isNullOrUndefOrEmpty(this.user.customStudio?.Studio?.Application?.[0]?.Links?.[0]?.$?.download)) {
                    const b64 = btoa(this.user.customStudio.Studio.Application[0].Links[0].$.download);
                    this.embedCustomHomePage = `${WEB_SERVER}:${DEFAULT_PORT}/proxyRequest/${b64}`;
                }
            })
        );
    }

    private refreshChart() {
        this.reloadCard = false;
        setTimeout(() => {
            this.reloadCard = true;
        });
    }

    private updateDataUsage(type) {
        this.dataUsageTotal = [
            {
                name: 'scenes',
                value: this.usage.scenesTotal ? this.usage.scenesTotal : 0
            },
            {
                name: 'resources',
                value: this.usage.resourceTotal ? this.usage.resourceTotal : 0
            },
            {
                name: 'stations',
                value: this.usage.stationsTotal ? this.usage.stationsTotal : 0
            },
            {
                name: 'paints',
                value: this.usage.paintTotal ? this.usage.paintTotal : 0
            },
            {
                name: 'catalog',
                value: this.usage.catalogTotal ? this.usage.catalogTotal : 0
            },
            {
                name: 'events',
                value: this.usage.eventsTotal ? this.usage.eventsTotal : 0
            },
            {
                name: 'transitions',
                value: this.usage.transitionsTotal ? this.usage.transitionsTotal : 0
            },
            {
                name: 'music',
                // tslint:disable-next-line
                value: this.usage.musicTotal ? parseInt(this.usage.musicTotal) : 0
            },
            {
                name: 'products',
                // tslint:disable-next-line
                value: this.usage.productsTotal ? parseInt(this.usage.productsTotal) : 0
            }
        ];
    }

    private updateDataBusiness() {
        this.dataBusiness = [
            {
                name: 'businesses',
                value: this.business.totalBusinesses
            },
            {
                name: 'users',
                value: this.business.totalUsers
            },
            {
                name: 'apps',
                value: this.business.apps
            },
            {
                name: 'installed',
                value: this.business.installedApps
            },
            {
                name: 'privileges',
                value: this.business.priveleges
            }
        ];
    }

    // private setTrackStationsSummary() {
    //     const dataConnected = [];
    //     const dataDisconnected = [];
    //     this.trackStations.forEach((v) => {
    //         const t = new Date(v.date);
    //         const tmpIndex = dataConnected.findIndex((w) => w.name.getTime() == t.getTime());
    //         if (tmpIndex > -1) {
    //             if (v.connection == 1) {
    //                 dataConnected[tmpIndex]['value']++;
    //             } else {
    //                 dataDisconnected[tmpIndex]['value']++;
    //             }
    //         } else {
    //             if (v.connection == 1) {
    //                 dataConnected.push({
    //                     name: t,
    //                     value: 1
    //                 });
    //                 dataDisconnected.push({
    //                     name: t,
    //                     value: 0
    //                 });
    //             } else {
    //                 dataConnected.push({
    //                     name: t,
    //                     value: 0
    //                 });
    //                 dataDisconnected.push({
    //                     name: t,
    //                     value: 1
    //                 });
    //             }
    //         }
    //     });
    //
    //     this.dataTrackStationsSummary = [
    //         {
    //             name: 'connected',
    //             series: dataConnected
    //         },
    //         {
    //             name: 'disconnected',
    //             series: dataDisconnected
    //         }
    //     ];
    // }

    // private setTrackStationsByConnection() {
    //     this.dataTrackStationsByConnection = [];
    //     this.trackStations.forEach((v) => {
    //
    //         // let item = new Date(v.date);
    //         const item = this.dataTrackStationsByConnection.find((w) => w.name == 'station ' + v.station_id);
    //         if (item) {
    //             const singleSeries = item['series'];
    //             if (v.connection == 1) {
    //                 singleSeries[0]['value']++;
    //             } else {
    //                 singleSeries[1]['value']++;
    //             }
    //         } else {
    //
    //             this.dataTrackStationsByConnection.push({
    //                 name: 'station ' + v.station_id,
    //                 series: [
    //                     {
    //                         name: 'connected',
    //                         value: v.connection ? 1 : 0
    //                     },
    //                     {
    //                         name: 'disconnected',
    //                         value: v.connection ? 0 : 1
    //                     }
    //                 ]
    //             });
    //         }
    //     });
    // }

    // private setDataTrackingStateByStatus() {
    //     let connected = 0;
    //     let disconnected = 0;
    //
    //     this.trackStations.forEach((v) => {
    //         connected += v.connection ? 1 : 0;
    //         disconnected += v.connection ? 0 : 1;
    //
    //         // if (this.dataTrackingStateByStatus.length > 0) {
    //         //     if (v.connection == 1) {
    //         //         this.dataTrackingStateByStatus[0]['value'] ++;
    //         //     } else {
    //         //         this.dataTrackingStateByStatus[1]['value'] ++;
    //         //     }
    //         // } else {
    //         // }
    //     });
    //
    //     this.dataTrackingStateByPercent = [
    //         {
    //             name: "connected",
    //             value: connected
    //         },
    //         {
    //             name: "disconnected",
    //             value: disconnected
    //         }
    //     ];
    //
    //     let rate = '#';
    //     const total = connected + disconnected;
    //     if (disconnected || connected) {
    //         rate = (Math.round(connected / total * 100)) + '% / ' + (Math.round(disconnected / total * 100)) + '%';
    //     }
    //     this.dataTrackingStateByStatus = [
    //         {
    //             name: "connected",
    //             value: connected
    //         },
    //         {
    //             name: "disconnected",
    //             value: disconnected
    //         },
    //         {
    //             name: 'total',
    //             value: connected + disconnected
    //         },
    //         {
    //             name: "rate",
    //             value: rate
    //         }
    //     ];
    // }

    onSelect(e) {
    }
}
